import classNames from 'classnames';
import {Button, BUTTON_SIZE} from 'components/Button';
import {Typography} from 'components/Typography';
import {CartProductsList} from 'modules/cart/components/CartProductsList';
import React, {FC, MouseEventHandler, useCallback, useEffect, useMemo, useRef} from 'react';
import {CART_ROUTE} from 'core/modules/routing/constants';
import {useAppDispatch} from 'core/redux/hooks/useAppDispatch';
import style from './style.module.scss';
import {actionSetAfterRemoveToast} from 'modules/cart/slice';
import {useClearCartMutation, useGetCartQuery} from 'modules/cart/services';
import {formatPrice} from 'core/helpers';
import {PriceProgressBar} from 'modules/cart/components/PriceProgressBar';
import {clearFlocktoryCart} from 'hooks/analytics/flocktory/flocktoryCartEvents';
import {useRouter} from 'next/router';
import CartBanner from 'modules/cart/components/CartBanner/CartBanner';
import {useGrowingDiscountGtag} from 'modules/product-card/analytics/gtag/hooks/useGrowingDiscountGtag';
import {MiniCartOpenGlobalState} from 'modules/cart/state/miniCartOpenGlobalState';

interface IProps {
    className?: string;
}

export const CartHeaderPopup: FC<IProps> = ({className}) => {
    const dispatch = useAppDispatch();
    const router = useRouter();

    const [clearCart] = useClearCartMutation();
    const {data: cartData} = useGetCartQuery({mini: true});

    const cartProductsList = useMemo(() => cartData?.products ?? [], [cartData?.products]);
    const discount = cartData?.total.discount;

    const productsListContainerRef = useRef<HTMLDivElement>(null);

    const handleCartClick = useCallback<MouseEventHandler<HTMLButtonElement>>(async () => {
        await router.push(CART_ROUTE);
    }, [router]);

    useEffect(() => {
        return () => {
            dispatch(actionSetAfterRemoveToast(''));
        };
    }, [dispatch]);

    const handleRemoveAllClick = useCallback<MouseEventHandler<HTMLButtonElement>>(async () => {
        await clearCart().then(() => clearFlocktoryCart());
    }, [clearCart]);

    const {handleMiniCartWidgetInView} = useGrowingDiscountGtag();

    const banner = useMemo(() => {
        return cartData?.banners;
    }, [cartData]);

    const [isMiniCartOpen] = MiniCartOpenGlobalState();

    useEffect(() => {
        if (!isMiniCartOpen || !banner || !cartProductsList.length) {
            return;
        }

        handleMiniCartWidgetInView(cartProductsList);
    }, [banner, cartProductsList, handleMiniCartWidgetInView, isMiniCartOpen]);

    return (
        <div className={classNames(style.cartHeaderPopupWrapper, className)}>
            <div className={style.cartHeaderPopupContainer}>
                <div className={style.cartHeaderPopup}>
                    <div className={style.cartHeaderPopupContent}>
                        <div className={style.cartHeaderPopupTitle}>
                            <Typography variant={'title-20-m'}>Корзина ({cartProductsList.length})</Typography>
                            <button className={style.removeAllButton} onClick={handleRemoveAllClick}>
                                <Typography variant={'label-16-r'}>Очистить</Typography>
                            </button>
                        </div>
                        <PriceProgressBar
                            percent={cartData?.minimalCartPrice?.percent}
                            left={cartData?.minimalCartPrice?.left}
                        />
                        {banner && <CartBanner />}
                        <div className={style.productsListContainer} ref={productsListContainerRef}>
                            <CartProductsList
                                cartProductsList={cartProductsList}
                                className={style.cartProductsList}
                                isForHeaderPopup={true}
                            />
                            <div className={style.totalContainer}>
                                {Boolean(discount) && (
                                    <div className={style.totalWrapper}>
                                        <Typography className={style.discount} variant={'label-16-r'}>
                                            Скидка
                                        </Typography>
                                        <Typography color={'primary100'} variant={'label-16-m'}>
                                            −{formatPrice(String(discount))} ₽
                                        </Typography>
                                    </div>
                                )}
                                {Boolean(cartData?.total?.price) && (
                                    <div className={style.totalWrapper}>
                                        <Typography variant={'price-18-b'}>Итого</Typography>
                                        <Typography variant={'price-18-b'}>
                                            {formatPrice(String(cartData?.total?.price))} ₽
                                        </Typography>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className={classNames(style.cartHeaderPopupTotal)}>
                            <Button className={style.toCartAction} onClick={handleCartClick} size={BUTTON_SIZE.small}>
                                Перейти в корзину
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
